/**
 * @file: MediaGridView.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import { EmptyListContent } from '@euler/app/components/list/EmptyListContent';
import { ImageViewer } from '@euler/app/components/media/ImageViewer';
import { useMediaCarousel } from '@euler/app/components/media/MediaCarousel';
import { MediaCoverView } from '@euler/app/components/media/MediaCoverView';
import { Typography } from '@euler/app/components/typography';
import { Center } from '@euler/components/Center';
import { Icon } from '@euler/components/Icomoon';
import { computeGridLayoutItemSize } from '@euler/functions/layoutMetrics';
import { MediaSourceTypeTextMap } from '@euler/functions/mediaSourceUtil';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useIsMobileLayout } from '@euler/functions/responsiveness';
import { useContainerWidth } from '@euler/functions/useAdaptiveContainerWidth';
import {
  MediaObject,
  MediaWithSource,
  mediaWithSourceToMediaObject,
} from '@euler/model';
import { MediaSourceType } from '@euler/model/report';
import { MotiView } from 'moti';
import { memo, useCallback, useMemo } from 'react';
import {
  FlatList,
  ListRenderItem,
  Platform,
  RefreshControl,
  ScrollViewProps,
  StyleProp,
  TouchableOpacity,
  View,
  ViewStyle,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

const kMargin = 5;
const kMaxWidth = 220;
const kLabelHeight = 22;

type Item = {
  item: MediaWithSource;
  media: MediaObject;
};

const keyExtractor = (item: Item) => item.media.id;

type MediaMode = 'view' | 'select';

export type MediaGridViewProps = {
  data: MediaWithSource[];
  loading?: boolean;
  refreshing?: boolean;
  error?: Error;
  mode?: MediaMode;
  selection?: MediaWithSource[];
  checkIconSize?: number;
  onRefresh?: () => void;
  onMediaSelected?: (media: MediaWithSource) => void;
  onMediaDeselected?: (media: MediaWithSource) => void;
};

export const MediaGridView = memo(
  ({
    data,
    mode = 'view',
    selection,
    loading,
    refreshing,
    error,
    contentContainerStyle,
    checkIconSize,
    onRefresh,
    onMediaSelected,
    onMediaDeselected,
    ...props
  }: MediaGridViewProps & Omit<ScrollViewProps, 'refreshControl'>) => {
    const { rpx } = useResponsiveContext();
    const { bottom } = useSafeAreaInsets();
    const width = useContainerWidth();
    const isMobile = useIsMobileLayout();
    const numberOfColumns = isMobile ? 2 : Math.floor(width / rpx(kMaxWidth));
    const cellMargin = rpx(kMargin);

    const [cellWidth, cellHeight] = computeGridLayoutItemSize({
      gridWidth: width,
      numberOfColumns,
      aspectRatio: 5 / 4,
      gridPadding: rpx(10),
      cellMargin,
    });
    const styles = useCellStyles({ cellWidth, cellHeight, cellMargin });

    const items = useMemo(
      () =>
        data?.map<Item>(x => ({
          item: x,
          media: mediaWithSourceToMediaObject(x),
        })) ?? [],
      [data],
    );

    const medias = useMemo(() => items.map(x => x.media), [items]);

    const {
      isImageViewerOpen,
      setIsImageViewerOpen,
      imageUrls,
      currentImageIndex,
      onMediaPress,
    } = useMediaCarousel(medias);

    const handlePress = useCallback(
      (media: MediaObject, index: number) => {
        if (mode === 'select') {
          if (!data[index]) return;
          if (selection?.some(x => x._id === data[index]._id)) {
            onMediaDeselected?.(data[index]);
          } else {
            onMediaSelected?.(data[index]);
          }
        } else {
          onMediaPress?.(media, index);
        }
      },
      [mode, data, selection, onMediaDeselected, onMediaSelected, onMediaPress],
    );

    const renderItem: ListRenderItem<Item> = useCallback(
      ({ item, index }) => {
        return (
          <Cell
            mode={mode}
            index={index}
            item={item.item}
            media={item.media}
            selected={selection?.some(x => x._id === item.item._id)}
            width={cellWidth}
            checkIconSize={checkIconSize}
            style={styles.cell}
            onPress={handlePress}
          />
        );
      },
      [mode, selection, cellWidth, checkIconSize, styles.cell, handlePress],
    );
    return (
      <>
        <FlatList
          key={numberOfColumns}
          numColumns={numberOfColumns}
          data={items}
          keyExtractor={keyExtractor}
          renderItem={renderItem}
          ListEmptyComponent={
            <EmptyListContent
              loading={data.length === 0 && loading}
              error={error}
              onRetry={onRefresh}
            />
          }
          refreshing={refreshing}
          refreshControl={
            Platform.OS === 'web' || !onRefresh ? undefined : (
              <RefreshControl
                refreshing={refreshing ?? false}
                onRefresh={onRefresh}
              />
            )
          }
          contentContainerStyle={[
            {
              backgroundColor: '#fff',
              paddingHorizontal: rpx(10),
              paddingTop: rpx(15),
              paddingBottom: rpx(15) + bottom,
              flexGrow: 1,
            },
            contentContainerStyle,
          ]}
          {...props}
        />
        <ImageViewer
          images={imageUrls}
          index={currentImageIndex}
          show={isImageViewerOpen}
          onDismiss={() => setIsImageViewerOpen(false)}
        />
      </>
    );
  },
);

const Cell = memo(
  ({
    item,
    mode,
    selected,
    media,
    style,
    width,
    index,
    checkIconSize,
    onPress,
  }: {
    item: MediaWithSource;
    selected?: boolean;
    mode: MediaMode;
    media: MediaObject;
    width: number;
    index: number;
    style?: StyleProp<ViewStyle>;
    checkIconSize?: number;
    onPress?: (media: MediaObject, index: number) => void;
  }) => {
    const { rpx } = useResponsiveContext();

    checkIconSize = checkIconSize ?? rpx(24);

    const handlePress = useCallback(() => {
      onPress?.(media, index);
    }, [index, media, onPress]);

    return (
      <MotiView
        from={{
          opacity: 0,
          translateY: rpx(50),
        }}
        animate={{
          opacity: 1,
          translateY: 0,
        }}
        transition={{
          type: 'timing',
          duration: 350,
        }}
        delay={Math.min(1500, index * 50)}
        style={style}
      >
        <MediaCoverView
          media={media}
          TouchableComponent={TouchableOpacity}
          onPress={handlePress}
          activityIndicatorSize={rpx(40)}
          noPlaceholder={media.type === 'image' && media.url.includes('#')}
          playIconSize={width / 6}
          containerStyle={{
            borderRadius: rpx(16),
            overflow: 'hidden',
          }}
          placeholderStyle={{
            backgroundColor: '#282828',
          }}
        >
          {media.title?.trim() || media.subTitle?.trim() ? (
            <View
              pointerEvents="none"
              css={`
                position: absolute;
                left: 0;
                right: 0;
                bottom: ${-rpx(0)}px;
                background-color: rgba(0, 0, 0, 0.55);
                min-height: ${rpx(50)}px;
                justify-content: center;
                padding-left: ${rpx(12)}px;
                padding-right: ${rpx(12)}px;
                padding-top: ${rpx(6)}px;
                padding-bottom: ${rpx(6)}px;
              `}
            >
              {media.title ? (
                <Typography.Label
                  medium
                  size={rpx(15)}
                  color={'rgba(255, 255, 255, 0.9)'}
                  numberOfLines={1}
                >
                  {media.title}
                </Typography.Label>
              ) : null}
              {media.subTitle ? (
                <Typography.Label
                  regular
                  size={rpx(14)}
                  color={'rgba(255, 255, 255, 0.7)'}
                  numberOfLines={1}
                >
                  {media.subTitle}
                </Typography.Label>
              ) : null}
            </View>
          ) : null}
        </MediaCoverView>
        <MediaSourceLabel source={item.source} />
        {mode === 'select' && (
          <Center.Touchable
            onPress={handlePress}
            style={{
              position: 'absolute',
              left: rpx(8),
              top: rpx(8),
              width: checkIconSize,
              height: checkIconSize,
              borderRadius: checkIconSize,
              backgroundColor: selected
                ? '#207FE7'
                : 'rgba(255, 255, 255, 0.75)',
            }}
          >
            {selected ? (
              <Icon name="check" color="white" size={checkIconSize * 0.75} />
            ) : (
              <View
                style={{
                  width: '100%',
                  height: '100%',
                  borderRadius: checkIconSize,
                  borderWidth: 1.5,
                  borderColor: '#207FE7',
                }}
              />
            )}
          </Center.Touchable>
        )}
      </MotiView>
    );
  },
);

const MediaSourceLabel = memo(
  ({
    source,
    style,
  }: {
    source: MediaSourceType;
    style?: StyleProp<ViewStyle>;
  }) => {
    const { rpx } = useResponsiveContext();
    return (
      <View
        style={style}
        css={`
          margin-top: ${rpx(4)}px;
        `}
      >
        <Typography.Label
          medium
          size={rpx(16)}
          color={'#333'}
          lineHeight={rpx(kLabelHeight)}
        >
          {MediaSourceTypeTextMap[source] ?? source}
        </Typography.Label>
      </View>
    );
  },
);

const useCellStyles = (args: {
  cellWidth: number;
  cellHeight: number;
  cellMargin: number;
}) => {
  const { rpx } = useResponsiveContext();
  const { cellWidth, cellHeight, cellMargin } = args;
  return {
    cell: {
      width: cellWidth,
      height: cellHeight + rpx(kLabelHeight) + rpx(4),
      marginLeft: cellMargin,
      marginRight: cellMargin,
      marginBottom: cellMargin * 3,
    },
    image: {
      width: cellWidth,
      height: cellHeight,
    },
  };
};
